import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useParams, useHistory } from 'react-router-dom';
import {isEmpty, isNil} from 'lodash';
// SHARED
import ModuleTitleBar from 'Components/shared/module-title-bar';
import { DEFAULT_MAX_PER_PAGE } from 'Components/shared/constants';
import { DriverProfileTwo } from 'Components/new-driver-management/driver-profile';
import { DriverList } from 'Components/new-driver-management/driver-list';
// HELPERS
import { decode, encode, toaster, toNum } from 'Helpers/index';
// services
import { driverActivation, fetchDiolkoCompaniesList, fetchDriver, fetchDriverList, fetchLogisticProviderList } from 'Services/new-driver-management.services';
// TYPES
import { DriverApprovalStatus, DriverStatus } from 'Types/new_driver_types';
import { Option } from 'Types/common.types';
// HOOKS
import { useSession, useQuery, useItemsPerPage, useAccessControlList } from 'Hooks/index';
import usePagination from "Hooks/pagination.hook";
import {useRecoilState} from "recoil";
import {GlobalFilterAtom} from "Recoil/global_filter.atom";


const DeactivatedDriver = () => {

  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const driverID = decode(id) || "";

  const { org, roleTypes } = useSession();
  const query = useQuery();
  const page = query.get('page') || 1;

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: 'Rider_Management'
  });
  // permissions
  const canList = permission.rights.includes("LIST");
  const canView = permission.rights.includes("VIEW");
  const canEdit = permission.rights.includes("EDIT");
  const canDelete = permission.rights.includes("DELETE");
  const [globalFilterState, ] = useRecoilState(GlobalFilterAtom);

  const initialFilters = {
    ancestorOrgIDs : globalFilterState?.selectedOrgIDs?.length ? globalFilterState?.selectedOrgIDs : null
  };
  const initialQueryParams = { page, offset: (toNum(page) - 1) * toNum(itemPerPage) }

  const [list, setList] = useState<any>([]);
  const [filters, setFilters] = useState<{ [key: string]: any }>(initialFilters);
  const [driverDetails, setDriverDetails] = useState<any>();
  const [companiesOption, setCompaniesOption] = useState<Option[]>([]);


  const [currentListCount, setCurrentListCount] = useState(0);
  const { paginationComponent, itemPerPage, queryParams, setQueryParams } = usePagination({
    currentListCount,
    resetPageDeps: filters
  });

  useEffect(() => {
    handleFetchDelectedDriverList();
  }, [queryParams.offset, filters, itemPerPage]);

  useEffect(()=>{
    if(!isEmpty(globalFilterState) && globalFilterState?.selectedOrgIDs?.length){
      setFilters({...filters, ancestorOrgIDs : globalFilterState?.selectedOrgIDs})
    }
  },[globalFilterState])

  useEffect(() => {
    if (driverID) {
      handleFetchDriverDetails();
    }
  }, [driverID]);

  useEffect(() => {
    if (org.orgType == "OWNER") handleFetchLogisticProviderList();
    if (org.orgType == "LOGISTIC_PROVIDER" || org.orgType == "OWNER") handleFetchCompaniesList();
  }, []);

  const tabKeyPaginationProps = {
    pathWithPage: {
      withoutTabKey: `/driver-management/driver/deleted?page=${page}`
    },
    pathWithoutPage: {
      withoutTabKey: '/driver-management/driver/deleted'
    },
  }

  const handleFetchLogisticProviderList = async () => {
    const responseData = await fetchLogisticProviderList(org._id);

    if (responseData && !responseData.error) {
      // set LP option atom
    } else {
      //
    }
  }

  const handleFetchCompaniesList = async () => {
    const responseData = await fetchDiolkoCompaniesList(org._id);

    if (responseData && !responseData.error) {
      const companyList = responseData.orgs;
      const companyOptions = companyList.map((company) => {
        return {
          label: company.orgName,
          value: company.orgID,
        }
      })
      setCompaniesOption(companyOptions);
    }
  }

  const handleFetchDriverDetails = async () => {
    const responseData = await fetchDriver(driverID);

    if (responseData && isNil(responseData.error)) {
      setDriverDetails(responseData);
    } else {
      toaster("error", responseData.error.message);
    }
  }

  const handleFetchDelectedDriverList = async () => {
    const responseData = await fetchDriverList(
      filters,
      [org._id],
      DriverApprovalStatus.APPROVED,
      true,
      undefined,
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
    );

    if (responseData && !responseData.error) {
      setList(responseData) ;
      setCurrentListCount(list.length) ;
    } else {
      toaster("error", responseData.error.message);
    }
  }

  const handleActivateDriver = async () => {
    const responseData = await driverActivation(driverDetails.profileID, false);

    if (responseData && !responseData.error) {
      history.push('/driver-management/driver/deleted');
      toaster("success", t("toaster.activateDriverSucess"));
    } else {
      toaster("error", responseData.error.message);
    }
  }

  const handleSetFilter = (input) => {
    setFilters({
      ...filters,
      orFields: input.orFields,
      vehicleType: input.vehicleType,
      companyOrgID: input.companiesFilter,
      riderType: input.driverType,
    })
  }

  return (
    <div>
      <Helmet>
        <title>{t('pageTitle.deletedDriver')}</title>
      </Helmet>

      <ModuleTitleBar
        titleText={t("title.driverManagement")}
        haveSubtitle
        subtitleText={t("title.inactiveDriver")}
      />

      {
        id ?
          (canView ? <DriverProfileTwo
            canEdit={canEdit}
            canDelete={canDelete}
            onBack={() => { history.goBack() }}
            driverDetails={driverDetails}
            onActivateDriver={handleActivateDriver}
          /> : null)
          :
          <DriverList
            canList={canList}
            canView={canView}
            canDelete={canDelete}
            canEdit={canEdit}
            driverList={list}
            companiesOption={companiesOption}
            onViewProfile={(profileID) => {
              const id = encode(profileID);
              history.push(`/driver-management/driver/deleted/${id}/view`);
            }}
            onFilter={handleSetFilter} 
          />
      }
      <div className="my-30">{paginationComponent}</div>
    </div>
  )
}

export default DeactivatedDriver
